import { useState } from 'react';
import { deliveriesApi, invoicesApi, lookupApi } from '../api';
import { useData, useSubmit } from '../hooks/useData';
import { Spinner, Modal, PageHeader, Field, Empty } from '../components/common';
import toast from 'react-hot-toast';
import { getBDDate } from '../utils/dateUtils';
import { downloadReport } from '../utils/downloadUtils';

const EMPTY = {
  inv_id: '', courier_id: '', courier_tracking_number: '',
  delivery_date: getBDDate(), amount_received: ''
};

export default function DeliveryPage() {
  const { data, loading, reload } = useData(deliveriesApi.list);
  const { data: invList }         = useData(invoicesApi.created);
  const { data: couriers }        = useData(lookupApi.couriers);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [selInv, setSelInv]       = useState(null);
  const [exporting, setExporting] = useState(false);

  const [doSubmit, saving] = useSubmit();

  const openAdd = () => { setForm(EMPTY); setSelInv(null); setModal('add'); };
  const close   = () => { setModal(null); setSelInv(null); };
  const set     = e  => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // When invoice selected → auto-fill amount_received with actual current due
  const handleInvChange = e => {
    const id  = e.target.value;
    const inv = invList.find(i => String(i.id) === String(id));
    setSelInv(inv || null);
    const currentDue = inv
      ? Math.max(0,
          (parseFloat(inv.total_price)     || 0) +
          (parseFloat(inv.delivery_charge) || 0) -
          (parseFloat(inv.advance)         || 0) -
          (parseFloat(inv.amount_paid)     || 0)
        )
      : 0;
    setForm(f => ({ ...f, inv_id: id, amount_received: String(currentDue.toFixed(2)) }));
  };

  const handleSave = () => {
    if (!form.inv_id)     { toast.error('Please select an invoice'); return; }
    if (!form.courier_id) { toast.error('Please select a courier');  return; }
    doSubmit(() => deliveriesApi.create(form), 'Delivery recorded — invoice marked delivered, stock updated', reload, close);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadReport(deliveriesApi.exportExcel, 'delivery-payment-list.xlsx');
    } catch {
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Delivery & Payments" subtitle="Record deliveries and collect payments"
        action={<button className="btn btn-primary btn-sm" onClick={openAdd}>+ New Delivery</button>} />

      <div className="card">
        <div className="card-header">
          <span className="card-title">Delivery Records</span>
          <button className="btn btn-secondary btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
        {loading ? <Spinner /> : (
          <div className="table-wrap">
            {data.length === 0 ? <Empty msg="No delivery records yet" /> : (
              <table className="responsive-table">
                <thead><tr>
                  <th>Invoice</th><th>Customer</th><th>Courier</th>
                  <th>Date</th><th>Payment</th><th>Due</th>
                </tr></thead>
                <tbody>{data.map(r => {
                  const due       = parseFloat(r.due || 0);
                  const totalPaid = Number(r.advance||0) + Number(r.amount_received||0);
                  return (
                    <tr key={r.id}>
                      <td data-label="Invoice">
                        <div className="td-primary font-mono">{r.inv_number}</div>
                        <div className="td-muted">৳{Number(r.total_price||0).toLocaleString()} total</div>
                      </td>
                      <td data-label="Customer">
                        <div className="td-primary">{r.customer_name}</div>
                        <div className="td-muted">{r.customer_mobile}</div>
                      </td>
                      <td data-label="Courier">
                        <div>{r.courier_name}</div>
                        {r.courier_tracking_number && (
                          <div className="td-muted font-mono">{r.courier_tracking_number}</div>
                        )}
                      </td>
                      <td data-label="Date" className="text-muted">{r.delivery_date}</td>
                      <td data-label="Payment">
                        <div className="font-mono text-success fw-500">৳{Number(r.amount_received).toLocaleString()} <span className="td-muted">this time</span></div>
                        <div className="td-muted font-mono">৳{totalPaid.toLocaleString()} total paid</div>
                      </td>
                      <td data-label="Due" className={`font-mono fw-500 ${due > 0 ? 'text-danger' : 'text-success'}`}>
                        ৳{due.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {modal && (
        <Modal title="New Delivery Entry" onClose={close}
          footer={<>
            <button className="btn btn-secondary" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>}>

          <Field label="Invoice" required>
            <select name="inv_id" value={form.inv_id} onChange={handleInvChange}>
              <option value="">Select invoice (status: created)…</option>
              {invList.map(i => {
                const due = Math.max(0,
                  (parseFloat(i.total_price) || 0) +
                  (parseFloat(i.delivery_charge) || 0) -
                  (parseFloat(i.advance) || 0) -
                  (parseFloat(i.amount_paid) || 0)
                );
                return (
                  <option key={i.id} value={i.id}>
                    {i.inv_number} — {i.customer_name} | Due: ৳{due.toLocaleString()}
                  </option>
                );
              })}
            </select>
          </Field>

          {/* Invoice breakdown when selected */}
          {selInv && (() => {
            const grandTotal = (parseFloat(selInv.total_price) || 0) + (parseFloat(selInv.delivery_charge) || 0);
            const advance    = parseFloat(selInv.advance)     || 0;
            const paid       = parseFloat(selInv.amount_paid) || 0;
            const currentDue = Math.max(0, grandTotal - advance - paid);
            return (
              <div style={{
                marginBottom: 16, padding: '14px 16px',
                background: 'var(--c-accent-tint)',
                border: '1px solid var(--c-border)',
                borderLeft: '3px solid var(--c-accent)',
                borderRadius: 'var(--r-sm)', fontSize: '.855rem'
              }}>
                <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--c-text)', fontSize: '.9rem' }}>
                  Invoice Payment Summary
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', color: 'var(--c-text-2)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span>Invoice Total:</span>
                    <strong className="font-mono">৳{Number(selInv.total_price || 0).toLocaleString()}</strong>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span>Delivery Charge:</span>
                    <strong className="font-mono">৳{Number(selInv.delivery_charge || 0).toLocaleString()}</strong>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span>Grand Total:</span>
                    <strong className="font-mono">৳{grandTotal.toLocaleString()}</strong>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span>Advance Paid:</span>
                    <strong className="font-mono">৳{advance.toLocaleString()}</strong>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span>Previously Received:</span>
                    <strong className="font-mono">৳{paid.toLocaleString()}</strong>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', color: currentDue > 0 ? 'var(--c-danger)' : 'var(--c-success)', fontWeight: 700 }}>
                    <span>Due Now:</span>
                    <span className="font-mono">৳{currentDue.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="form-row form-row-2">
            <Field label="Courier" required>
              <select name="courier_id" value={form.courier_id} onChange={set}>
                <option value="">Select courier…</option>
                {couriers.filter(c => c.active_yes_no).map(c =>
                  <option key={c.id} value={c.id}>{c.courier_name}</option>)}
              </select>
            </Field>
            <Field label="Tracking Number">
              <input type="text" name="courier_tracking_number"
                value={form.courier_tracking_number || ''}
                onChange={set} placeholder="Optional" />
            </Field>
          </div>

          <div className="form-row form-row-2">
            <Field label="Delivery Date">
              <input type="date" name="delivery_date" value={form.delivery_date} onChange={set} />
            </Field>
            <Field label="Amount Received (৳)">
              <input type="number" name="amount_received" value={form.amount_received}
                onChange={set} min="0" step="0.01" />
              {selInv && (() => {
                const grandTotal = (parseFloat(selInv.total_price) || 0) + (parseFloat(selInv.delivery_charge) || 0);
                const due = Math.max(0, grandTotal - (parseFloat(selInv.advance)||0) - (parseFloat(selInv.amount_paid)||0));
                const entered = parseFloat(form.amount_received) || 0;
                const remaining = due - entered;
                if (remaining > 0.01) return (
                  <span style={{ fontSize: '.75rem', color: 'var(--c-warning)', marginTop: 3, display: 'block' }}>
                    Partial payment — still due after this: ৳{remaining.toLocaleString()}
                  </span>
                );
                if (entered > 0) return (
                  <span style={{ fontSize: '.75rem', color: 'var(--c-success)', marginTop: 3, display: 'block' }}>
                    Fully paid
                  </span>
                );
                return null;
              })()}
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}